import React, { useRef, useEffect, useMemo, useState } from 'react';
import { 
  Bell, 
  X, 
  Check, 
  AlertTriangle, 
  Info, 
  RefreshCw, 
  User, 
  Target, 
  Trash2,
  Shield,
  Loader
} from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const NotificationPanel = () => {
  const { 
    notifications, 
    unreadCount, 
    isOpen, 
    isLoading,
    error,
    toggleNotifications, 
    closeNotifications,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    refreshNotifications
  } = useNotifications();
  
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const panelRef = useRef(null);
  
  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target) && isOpen) {
        closeNotifications();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, closeNotifications]);
  
  // Track if we should refresh on open (disabled by default to prevent reloading cleared notifications)
  const [shouldRefreshOnOpen, setShouldRefreshOnOpen] = useState(false);
  
  // Only refresh notifications when explicitly requested (not automatically on open)
  useEffect(() => {
    if (isOpen && shouldRefreshOnOpen) {
      refreshNotifications();
      setShouldRefreshOnOpen(false);
    }
  }, [isOpen, refreshNotifications, shouldRefreshOnOpen]);
  
  // Get icon based on notification type
  const getNotificationIcon = (notification) => {
    switch (notification.icon) {
      case 'CHECK':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'WARNING':
        return <AlertTriangle className="h-5 w-5 text-yellow-500" />;
      case 'ERROR':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'REFRESH':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'USER':
        return <User className="h-5 w-5 text-purple-500" />;
      case 'TARGET':
        return <Target className="h-5 w-5 text-blue-500" />;
      case 'SHIELD':
        return <Shield className="h-5 w-5 text-purple-500" />;
      default:
        return <Info className="h-5 w-5 text-blue-500" />;
    }
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString();
  };
  
  // Get background color based on priority
  const getBackgroundColor = (priority) => {
    switch (priority) {
      case 'SUCCESS':
        return 'bg-green-50 hover:bg-green-100';
      case 'WARNING':
        return 'bg-yellow-50 hover:bg-yellow-100';
      case 'ERROR':
        return 'bg-red-50 hover:bg-red-100';
      default:
        return 'bg-blue-50 hover:bg-blue-100';
    }
  };
  
  // Check if notification belongs to current user
  const isUserNotification = (notification) => {
    if (!user || !notification) return false;
    if (!notification.userId) return true; // No userId means it's for everyone
    
    // For system notifications
    if (notification.userId === 'system') return true;
    
    // Get safe user ID (string format)
    const safeUserId = typeof user.id === 'object' 
      ? (user.id._id || user.id.id || user.id.username || 'unknown') 
      : (user.id || 'guest');
    
    // Compare the notification's userId with the user's id
    // Convert both to strings for safer comparison
    const notificationUserId = typeof notification.userId === 'object'
      ? (notification.userId._id || notification.userId.id || notification.userId.toString()) 
      : notification.userId.toString();
    
    return notificationUserId === safeUserId;
  };
  
  // Filter notifications to show only relevant ones to the current user
  const filteredNotifications = useMemo(() => {
    if (!user || !Array.isArray(notifications) || notifications.length === 0) return [];
    
    // If user is admin, they can see all notifications
    if (isAdmin) {
      return notifications;
    }
    
    // Regular users can only see their own notifications and system notifications
    return notifications.filter(notification => {
      // Skip any malformed notifications
      if (!notification || typeof notification !== 'object') return false;
      
      // Always include system notifications
      if (notification.userId === 'system') return true;
      
      // Include user's own notifications
      return isUserNotification(notification);
    });
  }, [notifications, user, isAdmin]);
  
  // Handle "Clear All" button click
  const handleClearAll = async () => {
    try {
      await clearAllNotifications();
      // Don't refresh immediately as clearAllNotifications already updates the state
      // Reset the refresh flag so next time panel opens, it will refresh
      setShouldRefreshOnOpen(true);
    } catch (error) {
      console.error('Error clearing notifications:', error);
      // If clearing failed, refresh to show current state
      refreshNotifications();
    }
  };
  
  return (
    <div className="relative">
      {/* Notification Bell */}
      <button 
        className="relative p-2 text-gray-400 hover:text-white focus:outline-none"
        onClick={toggleNotifications}
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-xs text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>
      
      {/* Notification Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={panelRef}
            className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-lg shadow-lg overflow-hidden z-50"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {/* Header */}
            <div className="bg-gray-800 text-white p-4 flex justify-between items-center">
              <h3 className="font-medium">Notifications</h3>
              <div className="flex space-x-2">
                <button 
                  onClick={() => {
                    setShouldRefreshOnOpen(true);
                    refreshNotifications();
                  }}
                  className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded flex items-center"
                  aria-label="Refresh notifications"
                >
                  <RefreshCw className="h-3 w-3 mr-1" />
                  Refresh
                </button>
                {filteredNotifications.length > 0 && (
                  <button 
                    onClick={markAllAsRead}
                    className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
                    aria-label="Mark all as read"
                  >
                    Mark all read
                  </button>
                )}
                <button 
                  onClick={closeNotifications}
                  className="text-gray-300 hover:text-white"
                  aria-label="Close notifications"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Notification List */}
            <div className="max-h-96 overflow-y-auto">
              {isLoading ? (
                <div className="p-4 text-center text-gray-500">
                  <Loader className="h-8 w-8 mx-auto mb-2 opacity-50 animate-spin" />
                  <p>Loading notifications...</p>
                </div>
              ) : error ? (
                <div className="p-4 text-center text-amber-500">
                  <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
                  <p>{error}</p>
                  <div className="flex justify-center mt-2 space-x-2">
                    <button 
                      className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                      onClick={refreshNotifications}
                    >
                      Refresh
                    </button>
                  </div>
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No notifications</p>
                </div>
              ) : (
                <div>
                  <AnimatePresence>
                    {filteredNotifications.map((notification) => (
                      <motion.div
                        key={notification._id || notification.id}
                        className={`p-4 border-b border-gray-100 ${notification.read ? 'opacity-70' : ''} ${getBackgroundColor(notification.priority)}`}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                        onClick={() => markAsRead(notification._id || notification.id)}
                      >
                        <div className="flex">
                          <div className="flex-shrink-0 mr-3">
                            {getNotificationIcon(notification)}
                          </div>
                          <div className="flex-1">
                            <div className="flex justify-between">
                              <h4 className="text-sm font-medium text-gray-900">{notification.title}</h4>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  removeNotification(notification._id || notification.id);
                                }}
                                className="text-gray-400 hover:text-gray-600"
                                aria-label="Remove notification"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </div>
                            <p className="mt-1 text-sm text-gray-600">{notification.message}</p>
                            
                            {/* Show user info for admin */}
                            {isAdmin && !isUserNotification(notification) && notification.userId && (
                              <div className="mt-1 flex items-center">
                                <User className="h-3 w-3 text-gray-400 mr-1" />
                                <span className="text-xs text-gray-500">
                                  User: {typeof notification.userId === 'object' 
                                    ? (notification.userId.username || 'Unknown User') 
                                    : notification.userId}
                                </span>
                              </div>
                            )}
                            
                            {/* Progress bar for running scans */}
                            {notification.type === 'SCAN_RUNNING' && notification.progress !== undefined && (
                              <div className="mt-2 w-full bg-gray-200 rounded-full h-2.5">
                                <div 
                                  className="bg-blue-600 h-2.5 rounded-full" 
                                  style={{ width: `${notification.progress}%` }}
                                ></div>
                              </div>
                            )}
                            
                            <p className="mt-1 text-xs text-gray-500">{formatTimestamp(notification.timestamp)}</p>
                            
                            {!notification.read && (
                              <span className="inline-block mt-1 text-xs font-medium text-blue-600">New</span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>
            
            {/* Footer */}
            {filteredNotifications.length > 0 && (
              <div className="p-2 bg-gray-50 border-t border-gray-100 flex justify-end">
                <button 
                  onClick={handleClearAll}
                  className="flex items-center text-xs text-gray-600 hover:text-gray-900 px-2 py-1 rounded hover:bg-gray-200"
                >
                  <Trash2 className="h-3 w-3 mr-1" />
                  Clear all
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationPanel; 